import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../store/auth-context";
import { GetPremiumStatusForUser, GetUserById } from "../services/backend";
import { syncStripeSubscription } from "../services/stripeCheckout";
import { isUserPremium } from "../util/premiumAccess";

export function usePremiumAccess() {
  const { authenticatedUser, authenticateUser } = useContext(AuthContext);
  const userId = authenticatedUser?.id ?? null;
  const [isPremium, setIsPremium] = useState(() => isUserPremium(authenticatedUser));
  const [checking, setChecking] = useState(() => Boolean(userId));
  const authenticatedUserRef = useRef(authenticatedUser);
  const authenticateUserRef = useRef(authenticateUser);
  const refreshedForUserRef = useRef(null);
  authenticatedUserRef.current = authenticatedUser;
  authenticateUserRef.current = authenticateUser;

  const applyUser = useCallback((user) => {
    if (!user) {
      setIsPremium(false);
      return false;
    }
    const premium = isUserPremium(user);
    setIsPremium(premium);
    return premium;
  }, []);

  const refreshPremiumStatus = useCallback(async () => {
    const currentUser = authenticatedUserRef.current;
    const id = currentUser?.id;
    if (!id) {
      setIsPremium(false);
      setChecking(false);
      return false;
    }

    // Resolve from the already-loaded user first so Settings UI does not wait.
    if (applyUser(currentUser)) {
      setChecking(false);
      return true;
    }
    setChecking(true);
    try {
      const status = await GetPremiumStatusForUser(id);
      if (status.valid) {
        setIsPremium(true);
        const freshUser = await GetUserById(id);
        if (freshUser) authenticateUserRef.current(freshUser);
        return true;
      }

      try {
        await syncStripeSubscription(id);
        const syncedStatus = await GetPremiumStatusForUser(id);
        if (syncedStatus.valid) {
          setIsPremium(true);
          const freshUser = await GetUserById(id);
          if (freshUser) authenticateUserRef.current(freshUser);
          return true;
        }
      } catch {
        /* ignore sync errors */
      }

      setIsPremium(false);
      return false;
    } catch {
      setIsPremium(false);
      return false;
    } finally {
      setChecking(false);
    }
  }, [applyUser]);

  useEffect(() => {
    applyUser(authenticatedUser);
  }, [authenticatedUser, applyUser]);

  // Refresh once per user id — not on every authenticatedUser object replace.
  useEffect(() => {
    if (!userId) {
      refreshedForUserRef.current = null;
      setChecking(false);
      setIsPremium(false);
      return;
    }
    if (refreshedForUserRef.current === userId) return;
    refreshedForUserRef.current = userId;
    void refreshPremiumStatus();
  }, [userId, refreshPremiumStatus]);

  return { isPremium, checking, refreshPremiumStatus };
}

export async function refreshUserAfterCheckout(userId, authenticateUser, attempts = 6) {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      await syncStripeSubscription(userId);
    } catch {
      /* webhook may have already synced */
    }

    const freshUser = await GetUserById(userId);
    if (isUserPremium(freshUser)) {
      authenticateUser(freshUser);
      return true;
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
  }
  return false;
}
